'use client';

import React, { useEffect, useState } from 'react';

import {
    Align,
    Button,
    ButtonList,
    ButtonVariant,
    Icon,
    IconName,
    IconSize,
    Justify,
    Link,
    ModalBody,
    ModalFooter,
    Popover,
    PopoverDirection,
    RadioTile,
    RadioTiles,
    Row,
    Rows,
    Text,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { TradeInCriteriaResponsesType, TradeInCriteriaType } from '@app-types/trade-in';

import { Loader } from '@components/loader';

import { TradeInStep } from '@constants/trade-in';

import { getTradeInStep, setTradeInStep } from '@product-detail/helpers';

export type MobileTradeInStep2Props = {
    step: TradeInStep;
    setStep: (value: TradeInStep) => void;
    questions: TradeInCriteriaType;
    getQuote: (
        productId: string,
        criteria: TradeInCriteriaResponsesType['questions'],
        controller: AbortController,
    ) => void;
    isLoading: boolean;
    closeModal: () => void;
    isLoadingQuote: boolean;
};

export function MobileTradeInStep2({
    step,
    setStep,
    questions,
    getQuote,
    isLoading,
    isLoadingQuote,
    closeModal,
}: Readonly<MobileTradeInStep2Props>) {
    const [phoneName, setPhoneName] = useState('');
    const [responses, setResponses] = useState<TradeInCriteriaResponsesType['questions']>([]);

    const handleResponseChange = (question: TradeInCriteriaType[number], responseId: string) => {
        const responsesValues: TradeInCriteriaResponsesType['questions'] = [...responses];

        const answer = {
            id: question.id,
            reponse: { id: responseId, value: question.reponses.find((r) => r.id === responseId)!.label },
            question: question.label,
        };

        const index = responsesValues.findIndex((r) => r.id === question.id);

        if (index > -1) {
            responsesValues[index] = answer;
        } else {
            responsesValues.push(answer);
        }

        setTradeInStep(TradeInStep.FORM, responsesValues);
        setResponses(responsesValues);
    };

    const handleConfirmation = () => setStep(TradeInStep.CONFIRMATION);

    const handleReturn = () => setStep(TradeInStep.SEARCH);

    const hasResponseChecked = (questionId: string, responseId: string): boolean => {
        return responses.find((r) => r.id === questionId)?.reponse.id === responseId;
    };

    useEffect(() => {
        const product = getTradeInStep(TradeInStep.SEARCH);
        const valueStep = getTradeInStep(TradeInStep.FORM);
        if (phoneName !== product?.name) {
            setPhoneName(product?.name ?? '');
            setResponses([]);
        }
        if (responses.length <= 0 && valueStep) {
            setResponses(valueStep);
        }
    }, [step]);

    useEffect(() => {
        const controller = new AbortController();

        if (questions.length > 0 && responses.length === questions.length) {
            const valueStep = getTradeInStep(TradeInStep.SEARCH);
            if (valueStep) getQuote(valueStep.id, responses, controller);
        }

        return () => {
            controller.abort('Responses have changed');
        };
    }, [responses]);

    if (step !== TradeInStep.FORM) return null;

    return (
        <>
            <ModalBody>
                <Link role='button' onClick={handleReturn} className='is-flex is-aligned-center'>
                    <Icon name={IconName.ARROW_LEFT} />
                    &nbsp;Retour
                </Link>
                <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>{`Indiquez l'état de votre ${phoneName}.`}</Text>
                {isLoading ? (
                    <View flexable justify={Justify.CENTER}>
                        <Loader />
                    </View>
                ) : (
                    <Rows>
                        {questions.map((question) => (
                            <Row key={question.id}>
                                <View flexable align={Align.START} className='gap-medium'>
                                    <Text>{question.label}</Text>
                                    {question.reponses[0] && (
                                        <Popover
                                            trigger={<Icon name={IconName.INFOS_CIRCLE} size={IconSize.SMALLER} />}
                                            direction={PopoverDirection.LEFT}
                                        >
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: question.reponses[0].description.join('<br><br>'),
                                                }}
                                            />
                                        </Popover>
                                    )}
                                </View>
                                <RadioTiles className='grid-radio-list'>
                                    {question.reponses.map((reponse) => (
                                        <RadioTile
                                            horizontal
                                            label={reponse.label}
                                            checked={hasResponseChecked(question.id, reponse.id)}
                                            onChange={({ radioChecked }) =>
                                                radioChecked && handleResponseChange(question, reponse.id)
                                            }
                                            key={reponse.id}
                                            id={reponse.id}
                                        />
                                    ))}
                                </RadioTiles>
                            </Row>
                        ))}
                    </Rows>
                )}
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
                        Fermer
                    </Button>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        disabled={responses.length !== questions.length || isLoadingQuote}
                        onClick={handleConfirmation}
                        className={isLoadingQuote ? 'is-loading' : ''}
                    >
                        Étape suivante
                    </Button>
                </ButtonList>
            </ModalFooter>
        </>
    );
}
